/**
 * BroadcastSync - Communication between shell and pop-out windows
 *
 * Uses BroadcastChannel API to sync state changes across windows:
 * - Theme changes
 * - Tab open/close events
 * - Pop-out window lifecycle
 * - Session saves
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/BroadcastChannel
 */

export type BroadcastMessage =
  | { type: 'POPOUT_READY'; windowId: string }
  | { type: 'POPOUT_STATE_TRANSFER'; windowId: string; instanceId: string; state: unknown; title: string }
  | { type: 'POPOUT_CLOSED'; windowId: string; instanceId: string }
  | { type: 'THEME_CHANGED'; theme: 'light' | 'dark' }
  | { type: 'SESSION_SAVED'; timestamp: string }
  | { type: 'TAB_OPENED'; appId: string }
  | { type: 'TAB_CLOSED'; tabId: string }

type MessageCallback<T extends BroadcastMessage = BroadcastMessage> = (msg: T) => void

class BroadcastSync {
  private channel: BroadcastChannel
  private listeners: Map<string, Set<MessageCallback>> = new Map()
  private isInitialized = false

  constructor() {
    // Don't initialize in SSR environment
    if (typeof window === 'undefined') {
      this.channel = null as any
      return
    }

    this.channel = new BroadcastChannel('focus-shell')
    this.channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      const message = event.data

      // Dispatch to type-specific listeners
      const typeListeners = this.listeners.get(message.type)
      if (typeListeners) {
        typeListeners.forEach(callback => callback(message))
      }

      // Dispatch to wildcard listeners
      const wildcardListeners = this.listeners.get('*')
      if (wildcardListeners) {
        wildcardListeners.forEach(callback => callback(message))
      }
    }

    this.isInitialized = true
  }

  /**
   * Post a message to all other windows
   */
  postMessage(message: BroadcastMessage): void {
    if (!this.isInitialized) {
      console.warn('[BroadcastSync] Cannot post message, not initialized (SSR?)')
      return
    }

    this.channel.postMessage(message)
  }

  /**
   * Register a listener for a specific message type
   * Returns an unsubscribe function
   */
  on<T extends BroadcastMessage['type']>(
    type: T | '*',
    callback: MessageCallback<Extract<BroadcastMessage, { type: T }>>
  ): () => void {
    if (!this.isInitialized) {
      console.warn('[BroadcastSync] Cannot register listener, not initialized (SSR?)')
      return () => {}
    }

    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set())
    }

    const listeners = this.listeners.get(type)!
    listeners.add(callback as MessageCallback)

    // Return unsubscribe function
    return () => {
      listeners.delete(callback as MessageCallback)
      if (listeners.size === 0) {
        this.listeners.delete(type)
      }
    }
  }

  /**
   * Remove all listeners and close the channel
   */
  close(): void {
    if (!this.isInitialized) return

    this.listeners.clear()
    this.channel.close()
    this.isInitialized = false
  }

  /**
   * Check if BroadcastChannel is supported
   */
  isSupported(): boolean {
    return typeof window !== 'undefined' && 'BroadcastChannel' in window
  }
}

// Singleton instance
export const broadcastSync = new BroadcastSync()
